import _ from "lodash";
import {
  AUDIO_INSTRUCTIONS,
  RESOURCES,
  FINAL_REMARKS,
  OVERALL_ASSESSMENT_TOOL,
  INDIVIDUAL_ASSESSMENT_TOOL,
  ASSESSMENT_CRITERIA,
  ANSWER_TEXT,
  TEACHER_NOTES,
  TEXT_RESPONSE,
  SHORT_RESPONSE,
  LONG_RESPONSE,
  PRINT_DOTTED_LINES,
  QUESTION_UID_TEXT_RESPONSE_V3,
  FIELD_UID_ANSWER,
  FIELD_UID_TEACHER_NOTES,
  QUESTION_UID_GRAPHING,
  FIELD_UID_MCQ_ANSWERS,
  FIELD_UID_BLANKS_ANSWERS,
  QUESTION_UID_TRUE_FALSE_V2,
  QUESTION_UID_MULTI_CHOICE,
  FIELD_UID_RADIO,
  QUESTION_UID_BLANKS,
  FIELD_UID_ADDITIONAL_FIELD_SETTING,
  FIELD_UID_SETTINGS,
  QUESTION_UID_DRAWING,
  QUESTION_UID_DRAG_AND_DROP,
  QUESTION_UID_FIND_HOTSPOT,
  FIELD_UID_DRAG_AND_DROP_ANSWERS,
  FIELD_UID_FIND_HOTSPOT_ANSWERS,
  QUESTION_UID_LABELLING,
  FIELD_UID_LABELLING_ANSWERS,
  FIELD_UID_MATCHING,
  FIELD_UID_TABLE_ANSWERS,
  QUESTION_UID_AUDIO,
  QUESTION_UID_MATCHING,
  QUESTION_UID_TABLE,
  QUESTION_UID_VIDEO,
} from "Worksheet/modules/WorksheetConstants";
import longResponseImage from "Worksheet/icons/long_response_layout.svg";
import shortResponseImage from "Worksheet/icons/short_response_layout.svg";
import textResponseLayoutImage from "Worksheet/icons/text_response_layout.svg";
import { getMemoizedLangFunction } from "Utils";
import {
  getIsFieldEmpty,
  extractContentWithMediaFromHTML,
  extractTextWithLatex,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { checkEmptyElementValue } from "modules/ElementValueUtils";
import {
  TABLE_INPUT_END_CHAR,
  TABLE_INPUT_START_CHAR,
} from "UIComponents/Table/constants";

export const INSTRUCTION_DEFAULT_OPTIONS = [
  {
    toggleLabel: "worksheet:link_for_audio_instructions",
    value: AUDIO_INSTRUCTIONS,
    uid: AUDIO_INSTRUCTIONS,
  },
  {
    toggleLabel: "worksheet:links_for_resources",
    value: RESOURCES,
    uid: RESOURCES,
  },
];

export const EVALUATION_TOOL_DEFAULT_OPTIONS = [
  {
    toggleLabel: "worksheet:space_to_add_final_remarks",
    value: FINAL_REMARKS,
    uid: FINAL_REMARKS,
  },
  {
    toggleLabel: "worksheet:overall_assessment_tools_for_worksheet",
    value: OVERALL_ASSESSMENT_TOOL,
    uid: OVERALL_ASSESSMENT_TOOL,
  },
  {
    toggleLabel:
      "worksheet:assessment_tools_and_learning_goals_for_individual_question",
    value: INDIVIDUAL_ASSESSMENT_TOOL,
    uid: INDIVIDUAL_ASSESSMENT_TOOL,
  },
  {
    toggleLabel: "progressReport:assessment_criteria_descriptors",
    value: ASSESSMENT_CRITERIA,
    uid: ASSESSMENT_CRITERIA,
    dependentEntities: [OVERALL_ASSESSMENT_TOOL],
    tooltip: "worksheet:assessment_criteria_descriptor_tooltip",
  },
];

export const OTHER_ITEMS_DEFAULT_OPTIONS = [
  {
    toggleLabel: "academicSetup:answer_text",
    value: ANSWER_TEXT,
    uid: ANSWER_TEXT,
  },
  {
    toggleLabel: "common:teacher_notes",
    value: TEACHER_NOTES,
    uid: TEACHER_NOTES,
  },
];

export const questionLayoutDefaultOptions = getMemoizedLangFunction(({ t }) => {
  return {
    layoutDetails: [
      {
        title: t("worksheet:layout_text_that_requires_text_response"),
        subTitle: t("worksheet:text_response_layout_subtext"),
        image: textResponseLayoutImage,
        value: TEXT_RESPONSE,
      },
      {
        title: t(
          "worksheet:short_answer_layout_text_that_requires_text_response"
        ),
        subTitle: t("worksheet:text_response_layout_subtext_for_short_answer"),
        image: shortResponseImage,
        value: SHORT_RESPONSE,
      },
      {
        title: t(
          "worksheet:long_answer_layout_text_that_requires_text_response"
        ),
        subTitle: t("worksheet:text_response_layout_subtext_for_long_answer"),
        image: longResponseImage,
        value: LONG_RESPONSE,
      },
    ],
    settings: [
      {
        toggleLabel: "worksheet:layout_setting_text",
        value: PRINT_DOTTED_LINES,
        uid: PRINT_DOTTED_LINES,
      },
    ],
  };
});

export const getOtherItemOptions = (otherItems, t) => {
  return _.filter(
    OTHER_ITEMS_DEFAULT_OPTIONS,
    option => otherItems[option.value]
  );
};

export const getInstructionOptions = (instructions, t) => {
  return _.filter(
    INSTRUCTION_DEFAULT_OPTIONS,
    option => instructions[option.value]
  );
};

export const getEvaluationToolOptions = (evaluationTools, t) => {
  return _.filter(
    EVALUATION_TOOL_DEFAULT_OPTIONS,
    option => evaluationTools[option.value]
  );
};

/**
 * @description Checks if any of the learning goals fields in the assessment details have non-empty values.
 * @param {Object} assessmentDetails - The details of the assessment, including various fields and data.
 * @param {Array} learningGoalsFieldUid - An array of UIDs representing the learning goals fields to check.
 * @returns {Boolean} Returns `true` if any learning goals field has a non-empty value, otherwise `false`.
 */
const _getHasLearningGoals = ({
  assessmentDetails,
  learningGoalsFieldUid = [],
}) => {
  return _.some(learningGoalsFieldUid, value => {
    return checkEmptyElementValue({
      fieldKey: value,
      data: assessmentDetails,
      ignoreUnitPlanSubjectCheck: true,
      checkOnlyValueKeysForBenchmarks: true,
    });
  });
};

export const getHasLearningGoals = _.memoize(
  params => _getHasLearningGoals(params),
  ({ assessmentDetails, learningGoalsFieldUid }) => {
    return JSON.stringify({
      assessmentDetails,
      learningGoalsFieldUid: _.join(learningGoalsFieldUid, "|"),
    });
  }
);

/** @returns updated modal fields settings */
export const getUpdatedModalFieldsSettings = ({
  evaluationToolOptions,
  instructionOptions,
  otherItemOptions,
  selectedWorksheetDownloadModalSetting,
}) => {
  const instructionValues = _.map(instructionOptions, "value");
  const evaluationToolValues = _.map(evaluationToolOptions, "value");
  const otherItemValues = _.map(otherItemOptions, "value");

  const instructions = _.filter(
    _.get(selectedWorksheetDownloadModalSetting, "instructions"),
    value => _.includes(instructionValues, value)
  );
  const evaluationTools = _.filter(
    _.get(selectedWorksheetDownloadModalSetting, "evaluationTools"),
    value => _.includes(evaluationToolValues, value)
  );
  const otherItems = _.filter(
    _.get(selectedWorksheetDownloadModalSetting, "otherItems"),
    value => _.includes(otherItemValues, value)
  );
  const questionLayout = _.get(
    selectedWorksheetDownloadModalSetting,
    "questionLayout"
  );

  return {
    instructions,
    evaluationTools,
    otherItems,
    questionLayout,
  };
};

/** @returns boolean whether question has answer */
export const getHasAnswer = ({ questionTypeUid, fields }) => {
  const showDocEditor = getIsWorksheetDocEditorEnabled();
  switch (questionTypeUid) {
    case QUESTION_UID_TRUE_FALSE_V2: {
      const correctAnswer = _.get(
        _.find(fields, { uid: FIELD_UID_RADIO }),
        "value.correct"
      );
      return !_.isEmpty(correctAnswer);
    }
    case QUESTION_UID_MULTI_CHOICE: {
      const correctOptions = _.get(
        _.find(fields, {
          uid: FIELD_UID_MCQ_ANSWERS,
        }),
        "value",
        []
      );
      return !_.isEmpty(_.filter(correctOptions, "isSelected"));
    }
    case QUESTION_UID_BLANKS: {
      const answerField = _.find(fields, {
        uid: FIELD_UID_BLANKS_ANSWERS,
      });
      const answers = _.get(answerField, "value.answers");
      return !_.isEmpty(answers);
    }
    case QUESTION_UID_LABELLING:
    case QUESTION_UID_DRAG_AND_DROP:
    case QUESTION_UID_FIND_HOTSPOT: {
      const answerField = _.find(fields, {
        uid:
          questionTypeUid === QUESTION_UID_LABELLING
            ? FIELD_UID_LABELLING_ANSWERS
            : questionTypeUid === QUESTION_UID_DRAG_AND_DROP
            ? FIELD_UID_DRAG_AND_DROP_ANSWERS
            : FIELD_UID_FIND_HOTSPOT_ANSWERS,
      });
      return !_.isEmpty(_.get(answerField, "value", null));
    }
    case QUESTION_UID_TABLE: {
      let hasAnswer = false;
      const responseValue = _.get(
        _.find(fields, { uid: FIELD_UID_TABLE_ANSWERS }),
        "value",
        ""
      );
      _.forEach(responseValue, row => {
        _.forEach(row, (col, id) => {
          if (_.isEqual(id, "id")) return;
          const colVal = extractTextWithLatex(col);
          if (
            !_.isEmpty(colVal.substring(1, _.size(colVal) - 1)) &&
            _.startsWith(colVal, TABLE_INPUT_START_CHAR) &&
            _.endsWith(colVal, TABLE_INPUT_END_CHAR)
          )
            hasAnswer = true;
        });
      });
      return hasAnswer;
    }
    case QUESTION_UID_MATCHING: {
      let hasAnswer = false;
      const matchingPairs = _.get(
        _.find(fields, item => {
          return _.get(item, "uid", "") === FIELD_UID_MATCHING;
        }),
        "value.matchingPairs",
        []
      );
      hasAnswer = _.some(matchingPairs, item => {
        const answerValue = _.get(item, "answer.value", "");
        return !_.isEmpty(
          showDocEditor
            ? extractContentWithMediaFromHTML({
                htmlString: _.toString(answerValue),
              })
            : answerValue
        );
      });

      return hasAnswer;
    }
    default:
      return false;
  }
};

/**
 * @deprecated use getQuestionDetailsV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getQuestionDetails = ({
  question,
  subQuestions,
  settingDetails = {},
  curriculumProgramType,
  learningGoalsFieldUid,
}) => {
  const questionData = _.get(question, "item", {});
  const assessmentTools = _.get(questionData, "assessmentTools", []);
  const questionTypeUid = _.get(questionData, "questionType.uid");
  const fields = _.concat(
    _.get(questionData, "fields", []),
    _.get(questionData, "answerFields", [])
  );
  const settings = _.get(
    _.find(fields, { uid: FIELD_UID_SETTINGS }),
    "value",
    {}
  );
  const hasResponseField = _.get(
    settings,
    FIELD_UID_ADDITIONAL_FIELD_SETTING,
    false
  );

  const hasLearningGoals = getHasLearningGoals({
    assessmentDetails: {
      allFields: fields,
    },
    learningGoalsFieldUid,
  });
  const hasAssessmentTools = _.size(_.get(assessmentTools, "edges", [])) > 0;

  const hasResponseText =
    _.includes(
      [
        QUESTION_UID_TEXT_RESPONSE_V3,
        QUESTION_UID_AUDIO,
        QUESTION_UID_VIDEO,
        QUESTION_UID_GRAPHING,
      ],
      questionTypeUid
    ) ||
    (_.includes(
      [QUESTION_UID_TRUE_FALSE_V2, QUESTION_UID_DRAWING],
      questionTypeUid
    ) &&
      hasResponseField);

  if (hasResponseText) {
    settingDetails["hasTextResponseBasedQuestion"] = true;
  }
  if (hasAssessmentTools || hasLearningGoals) {
    settingDetails["hasQuestionLevelEvaluationTools"] = true;
  }
  const answerText = _.get(
    _.find(fields, { uid: FIELD_UID_ANSWER }),
    "value",
    null
  );

  const hasAnswer = getHasAnswer({ questionTypeUid, fields });
  const hasAnswerText = !getIsFieldEmpty({
    value: answerText,
    fieldUid: FIELD_UID_ANSWER,
  });
  if (hasAnswer || hasAnswerText) {
    settingDetails["hasAnswerText"] = true;
  }

  const teacherNotes = _.get(
    _.find(fields, { uid: FIELD_UID_TEACHER_NOTES }),
    "value",
    null
  );
  if (
    !getIsFieldEmpty({
      value: teacherNotes,
      fieldUid: FIELD_UID_TEACHER_NOTES,
    })
  ) {
    settingDetails["hasTeacherNotes"] = true;
  }

  _.map(subQuestions, subQuestion => {
    return getQuestionDetails({
      question: subQuestion,
      subQuestions: [],
      settingDetails,
      curriculumProgramType,
      learningGoalsFieldUid,
    });
  });
};

/**
 * @deprecated use getSettingDetailsV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
/** @returns worksheet level setting details */
export const getSettingDetails = ({
  sections,
  curriculumProgramType,
  learningGoalsFieldUid,
}) => {
  const settingDetails = {
    hasTextResponseBasedQuestion: false,
    hasAnswerText: false,
    hasTeacherNotes: false,
    hasQuestionLevelEvaluationTools: false,
  };

  _.forEach(sections, section => {
    const sectionQuestions = _.map(
      _.get(section, "childrenV2.edges", []),
      "node"
    );

    _.map(sectionQuestions, question => {
      const subQuestions = _.map(
        _.get(question, "childrenV2.edges", []),
        "node"
      );

      getQuestionDetails({
        question,
        subQuestions,
        settingDetails,
        curriculumProgramType,
        learningGoalsFieldUid,
      });
    });
  });
  return settingDetails;
};

/** @returns a boolean whether any settings are applicable */
export const getHasApplicableSettings = modalFields => {
  return _.some(_.keys(modalFields), key => {
    const value = _.get(modalFields, key);
    if (_.isBoolean(value)) return value;
    else return !_.isEmpty(value);
  });
};

export const getTruthyKeysFromObject = (data, key) => {
  const value = _.get(data, `params.${key}`);
  return _.keys(_.pickBy(value, Boolean));
};

export const arrayKeysToTruthyObject = data => {
  return _.reduce(
    data,
    (result, key) => {
      result[key] = true;
      return result;
    },
    {}
  );
};
/**
 * Gets question details for determining relevant settings for worksheet download
 *
 * @param {Object} options - The configuration options
 * @param {Object} options.question - The question data object
 * @param {Object} [options.settingDetails={}] - Object to store settings flags
 * @param {Array} options.learningGoalsFieldUid - Array of UIDs for learning goals fields
 * @returns {void} - Modifies the settingDetails object by reference
 */
export const getQuestionDetailsV2 = ({
  question,
  settingDetails = {},
  learningGoalsFieldUid,
}) => {
  // Extract question metadata
  const assessmentTools = _.get(question, "assessmentTools", []);
  const questionTypeUid = _.get(question, "questionType.uid");
  const fields = _.concat(
    _.get(question, "fields", []),
    _.get(question, "answerFields", [])
  );

  // Get settings from fields
  const settings = _.get(
    _.find(fields, { uid: FIELD_UID_SETTINGS }),
    "value",
    {}
  );
  const hasResponseField = _.get(
    settings,
    FIELD_UID_ADDITIONAL_FIELD_SETTING,
    false
  );

  // Check for learning goals and assessment tools
  const hasLearningGoals = getHasLearningGoals({
    assessmentDetails: {
      allFields: fields,
    },
    learningGoalsFieldUid,
  });
  const hasAssessmentTools = _.size(_.get(assessmentTools, "edges", [])) > 0;

  // Determine if question has text response based on question type
  const hasResponseText =
    _.includes(
      [
        QUESTION_UID_TEXT_RESPONSE_V3,
        QUESTION_UID_AUDIO,
        QUESTION_UID_VIDEO,
        QUESTION_UID_GRAPHING,
      ],
      questionTypeUid
    ) ||
    (_.includes(
      [QUESTION_UID_TRUE_FALSE_V2, QUESTION_UID_DRAWING],
      questionTypeUid
    ) &&
      hasResponseField);

  // Update settings based on question properties
  if (hasResponseText) {
    settingDetails["hasTextResponseBasedQuestion"] = true;
  }
  if (hasAssessmentTools || hasLearningGoals) {
    settingDetails["hasQuestionLevelEvaluationTools"] = true;
  }

  // Check for answer text
  const answerText = _.get(
    _.find(fields, { uid: FIELD_UID_ANSWER }),
    "value",
    null
  );

  const hasAnswer = getHasAnswer({ questionTypeUid, fields });
  const hasAnswerText = !getIsFieldEmpty({
    value: answerText,
    fieldUid: FIELD_UID_ANSWER,
  });
  if (hasAnswer || hasAnswerText) {
    settingDetails["hasAnswerText"] = true;
  }

  // Check for teacher notes
  const teacherNotes = _.get(
    _.find(fields, { uid: FIELD_UID_TEACHER_NOTES }),
    "value",
    null
  );
  if (
    !getIsFieldEmpty({
      value: teacherNotes,
      fieldUid: FIELD_UID_TEACHER_NOTES,
    })
  ) {
    settingDetails["hasTeacherNotes"] = true;
  }
};

/** @returns worksheet level setting details */

/** @returns worksheet level setting details */
export const getSettingDetailsV2 = ({
  data,
  curriculumProgramType,
  learningGoalsFieldUid,
}) => {
  const settingDetails = {
    hasTextResponseBasedQuestion: false,
    hasAnswerText: false,
    hasTeacherNotes: false,
    hasQuestionLevelEvaluationTools: false,
  };

  _.forEach(data, section => {
    const sectionQuestions = _.get(section, "children", []);

    _.map(sectionQuestions, question => {
      const subQuestions = _.get(question, "children", []);

      getQuestionDetails({
        question,
        subQuestions,
        settingDetails,
        curriculumProgramType,
        learningGoalsFieldUid,
      });
    });
  });
  return settingDetails;
};

export const getSettingsInfoFromData = ({
  data,
  isQueriesV2Enabled,
  baseInstructionSettings,
  baseEvaluationToolSettings,
  curriculumProgramType,
  learningGoalsFieldUid,
}) => {
  let settingDetails = {};

  if (isQueriesV2Enabled) {
    // Use V2 enhancer data
    settingDetails = getSettingDetailsV2({
      data,
      curriculumProgramType,
      learningGoalsFieldUid,
    });
  } else {
    //WORKSHEET_RESTRUCTURING:Remove else part
    settingDetails = getSettingDetails({
      sections: data,
      curriculumProgramType,
      learningGoalsFieldUid,
    });
  }

  // Compute question-dependent settings
  const hasTextResponseBasedQuestion = _.get(
    settingDetails,
    "hasTextResponseBasedQuestion"
  );

  const answerBookletSettings = {
    [ANSWER_TEXT]: _.get(settingDetails, "hasAnswerText"),
    [TEACHER_NOTES]: _.get(settingDetails, "hasTeacherNotes"),
  };

  const instructionSettings = {
    ...baseInstructionSettings,
  };

  const evaluationToolSettings = {
    ...baseEvaluationToolSettings,
    [INDIVIDUAL_ASSESSMENT_TOOL]: _.get(
      settingDetails,
      "hasQuestionLevelEvaluationTools"
    ),
  };

  return {
    hasTextResponseBasedQuestion,
    answerBookletSettings,
    instructionSettings,
    evaluationToolSettings,
  };
};
